/**
 * Response Handler Utility for OpenAPI SDK
 *
 * Provides consistent error handling across all API files.
 * Extracts backend error messages and throws proper errors for TanStack Query.
 *
 * @see Context7 research: /websites/openapi-ts_dev - Error handling patterns
 * @see Context7 research: /react-hook-form/react-hook-form - Server-side validation error handling
 */

/**
 * Custom error class that preserves field-level validation errors from the backend
 *
 * This class extends the standard Error to include an optional `errors` object
 * containing field-specific validation messages. This enables react-hook-form's
 * `setError()` method to display validation errors below individual form inputs.
 *
 * @example Backend response structure:
 * ```json
 * {
 *   "message": "Eroare la validarea datelor. Verifica datele trimise.",
 *   "errors": {
 *     "telefon": "Numarul de telefon trebuie să contina exact 10 cifre.",
 *     "email": "Email-ul este invalid."
 *   }
 * }
 * ```
 *
 * @example Usage in form hooks — fara `any`, fara acces pe orbeste:
 * ```typescript
 * onError: (error) => {
 *   // Erorile pe campuri ajung direct in formular
 *   applyServerFieldErrors(error, form.setError);
 *
 *   // Mesajul general, cu fallback
 *   toastOneError(getErrorMessage(error, 'Salvarea a esuat.'));
 * }
 * ```
 *
 * Helper-ele sunt mai jos in acest fisier (`getErrorMessage`, `getErrorStatus`,
 * `getFieldErrors`, `isApiValidationError`); `applyServerFieldErrors` e in
 * `shared/utils/errors.ts`, ca sa nu legam stratul de API de react-hook-form.
 */
export class ApiValidationError extends Error {
  errors?: Record<string, string>;
  status?: number;
  /**
   * Fragmentele pe care serverul nu le-a inteles, cand le trimite (azi: 400 pe
   * `POST /api/answers`, campul `fragmenteNerecunoscute` din `ErrorResponse`).
   *
   * 🔴 De ce e nevoie de un camp ANUME, si nu de „tot corpul erorii": pana acum `handleResponse`
   * copia exact trei proprietati (`message`, `errors`, `status`), deci orice camp nou sosea pe
   * sarma si murea aici — tacut, fara eroare, fara sa lipseasca nimic vizibil. Un contract corect
   * si un contract UTILIZABIL sunt doua lucruri diferite; asta era a treia granita la care se
   * pierde diferenta, dupa transformerele care nu ruleaza si specul care nu descrie corpul de
   * eroare.
   *
   * ⚠️ Se copiaza UN camp, nu corpul. Copiat intreg, granita ar dispărea cu totul: orice ar
   * trimite serverul ar ajunge la componente, si n-am mai avea unde verifica ce intra. Exista
   * test care cere ca un camp din afara contractului sa NU treaca (control negativ cerut de B1).
   */
  fragmenteNerecunoscute?: string[];
  /**
   * De ce a refuzat serverul, ca eticheta de masina (azi: rutele de cod unic — `COD_EXPIRAT`,
   * `COD_FOLOSIT`, `COD_INVALID` pe `/otp/verify`; `LIMITA_COD`, `LIMITA_IP`, `TRIMITERE_ESUATA`
   * pe `/otp/inscriere`). Aceeasi regula ca la `fragmenteNerecunoscute`: UN camp anume, copiat doar
   * daca e sir nevid. Valorile nu se valideaza aici — stratul asta nu stie de domenii; cine il
   * citeste compara cu enumul generat si trateaza orice altceva ca necunoscut.
   */
  motiv?: string;
  /** Secunde pana cand se poate cere un cod nou (429 `LIMITA_COD`); doar numar finit, nenegativ. */
  secundePanaLaCodNou?: number;
  /** Mesajul original al backendului, cand a fost inlocuit cu unul romanesc generic.
   *  Pentru telemetrie si depanare — NU se afiseaza. */
  mesajOriginal?: string;

  constructor(message: string, errors?: Record<string, string>, status?: number) {
    super(message);
    this.name = 'ApiValidationError';
    this.errors = errors;
    this.status = status;
  }
}

/**
 * Handles OpenAPI SDK responses - throws on error, returns data on success
 *
 * The OpenAPI SDK returns responses in the format:
 * - Success: { data: T, error: undefined }
 * - Error: { data: undefined, error: { message, detail, title, errors?, ... } }
 *
 * This helper:
 * 1. Checks for error presence
 * 2. Extracts backend error message (message > detail > title > fallback)
 * 3. Extracts field-level errors object if present (for form validation)
 * 4. Throws ApiValidationError with both message and errors (caught by TanStack Query onError)
 * 5. Returns typed data if successful
 *
 * @param response - SDK response with data/error structure
 * @returns Data if successful
 * @throws ApiValidationError with backend message and field errors if failed
 *
 * @example
 * ```typescript
 * async getById(id: string): Promise<SchoolDto> {
 *   const response = await getSchoolById({ path: { id } });
 *   return handleResponse(response);
 * }
 * ```
 */
/**
 * Eroare de TRANSPORT: cererea nu a ajuns niciodata la backend.
 *
 * De ce exista un tip separat: clientul generat (`api/generated/client/client.gen.ts:106`)
 * face `e.error = e.response?.data ?? {}`. La o cadere de retea nu exista raspuns, deci
 * `e.error` devine `{}` — un obiect **truthy**. `handleResponse` intra pe ramura de eroare,
 * nu gaseste nimic in el, si pana acum imbraca in `ApiValidationError` ceva ce nu atinsese
 * backendul. Consecinta: garda `getSafeErrorMessage`, scrisa exact ca sa distinga cele doua
 * cazuri, le vedea identice, iar rezervele romanesti ale apelantilor erau cod inaccesibil.
 * Vezi `docs/knowledge/rezerva-de-eroare-inaccesibila.md`.
 *
 * ⚠️ Mesajul e in ROMANA din constructie, si asta e partea care repara cei ~74 de apelanti
 * fara sa-i atinga: majoritatea folosesc `getErrorMessage`, care afiseaza `.message`-ul
 * oricarui `Error`. Un mesaj tehnic englezesc pus aici ar scurge exact ce inchidem.
 */
export class ApiTransportError extends Error {
    /** Mesajul tehnic al lui axios („Network Error", „timeout of 5000ms exceeded"), pentru
     *  telemetrie. NU se afiseaza — de-aia nu e `message`. */
    mesajOriginal?: string;

    constructor(mesajOriginal?: string) {
        super('Nu am putut contacta serverul. Verifică conexiunea la internet și încearcă din nou.');
        this.name = 'ApiTransportError';
        this.mesajOriginal = mesajOriginal;
    }
}

export function isApiTransportError(error: unknown): error is ApiTransportError {
    return error instanceof ApiTransportError;
}

/**
 * Mesajul romanesc generic, pe cod HTTP, folosit cand backendul nu ne da unul folosibil.
 * Corpul implicit al Spring e in engleza („Forbidden", „Internal Server Error"), iar din
 * 2026-09-05 site-ul e 100 % in romana (mandat de la owner).
 */
const MESAJE_PE_STATUS: Record<number, string> = {
    400: 'Datele trimise nu sunt corecte. Verifică-le și încearcă din nou.',
    401: 'Sesiunea a expirat. Autentifică-te din nou.',
    403: 'Nu ai drepturile necesare pentru această acțiune.',
    404: 'Nu am găsit ce ai cerut.',
    409: 'Există deja o înregistrare cu aceste date.',
    413: 'Fișierul trimis este prea mare.',
    422: 'Datele trimise nu sunt corecte. Verifică-le și încearcă din nou.',
    429: 'Prea multe cereri. Așteaptă câteva momente și încearcă din nou.',
    500: 'A apărut o problemă pe server. Încearcă din nou peste câteva momente.',
    502: 'A apărut o problemă pe server. Încearcă din nou peste câteva momente.',
    503: 'A apărut o problemă pe server. Încearcă din nou peste câteva momente.',
    504: 'A apărut o problemă pe server. Încearcă din nou peste câteva momente.'
};

export function mesajGenericRomanesc(status?: number): string {
    return (status !== undefined && MESAJE_PE_STATUS[status])
        || 'Cererea nu a putut fi procesată. Încearcă din nou.';
}

/** Fraze HTTP/Spring standard, comparate pe mesajul INTREG (nu pe bucati). */
const FRAZE_STANDARD = [
    'bad request', 'unauthorized', 'forbidden', 'not found', 'method not allowed',
    'conflict', 'payload too large', 'unsupported media type', 'too many requests',
    'internal server error', 'bad gateway', 'service unavailable', 'gateway timeout',
    'access denied', 'no message available', 'network error', 'request failed'
];

/** Tipare tehnice cu numar variabil in ele, deci necomparabile ca fraza fixa (axios). */
const TIPARE_TEHNICE = [
    /^request failed with status code \d+$/i,
    /^timeout of \d+ms exceeded$/i,
    /^http error \d+$/i
];

/** Amprenta de Java/Spring, cautata oriunde in mesaj. */
const AMPRENTA_TEHNICA =
    /(exception|java\.|jakarta\.|javax\.|org\.springframework|could not execute statement|constraint \[|\bat com\.|stacktrace)/i;

/**
 * Cuvinte romanesti frecvente in mesajele backendului, ca semn ca mesajul E in romana.
 *
 * 🔴 De ce nu e de ajuns testul pe diacritice: backendul scrie curent romaneste FARA ele —
 * „Eroare la validarea datelor. Verifica datele trimise.", „Acces interzis.". O heuristica
 * pe diacritice ar fi ascuns exact mesajele de validare care trebuie citite.
 *
 * Lista e deliberat scurta si fara cuvinte care exista si in engleza („date", „invalid",
 * „nu" singur), ca sa nu declare romanesc un mesaj englezesc.
 *
 * 🔴 Se compara pe CUVANT INTREG, nu pe subsir. „acces" e prefixul lui „access": cu `includes`,
 * mesajul Spring „Access Denied" iesea romanesc si scapa neinlocuit pe ecran. Prins de test.
 */
/** Clasa de DETECTIE contine si variantele cu sedila (U+015F, U+0163), nu doar cele cu
 *  virgula (U+0219, U+021B): mesajele vechi ale backendului pot fi scrise cu oricare.
 *  Sirurile pe care le AFISAM noi folosesc exclusiv virgula, ca restul UI-ului. */
const SEMN_ROMANESC = /[ăâîșțşţ]/i;

const CUVINTE_ROMANESTI = [
    'eroare', 'este', 'sunt', 'trebuie', 'exista', 'acces', 'interzis', 'parola', 'parolă',
    'utilizator', 'scoala', 'școala', 'elev', 'profesor', 'contul', 'adresa', 'campul',
    'câmpul', 'verifica', 'verifică', 'incearca', 'încearcă', 'deja', 'lipseste', 'lipsește',
    'valoarea', 'cererea', 'gasit', 'găsit', 'permis', 'nu ai', 'nu se', 'nu a', 'nu este'
];

/** Cuvintele de mai sus, ca tipar pe cuvant intreg. */
const TIPAR_CUVINTE_ROMANESTI = new RegExp(
    '(^|[^a-zăâîșț])(' + CUVINTE_ROMANESTI.join('|') + ')([^a-zăâîșț]|$)', 'i');

/**
 * Mesajul pare tehnic-englezesc, deci nu are ce cauta pe ecran?
 *
 * ⚠️ DIRECTIA ERORII, declarata: heuristica greseste spre **a lasa mesajul backendului**.
 * Un englezism scapat e vizibil si reparabil; o eroare de validare romaneasca ascunsa in
 * spatele unui mesaj generic e invizibila si il lasa pe utilizator fara instructiune.
 * De-aia e denylist (fraza standard SAU amprenta Java) SI absenta oricarui semn romanesc,
 * nu allowlist.
 *
 * Limita cunoscuta, fixata in teste: un mesaj romanesc care CONTINE text tehnic
 * („Eroare la salvare: NullPointerException") ramane neatins — are semn romanesc. Curatarea
 * lui se face in backend, unde se concateneaza, nu aici.
 */
export function pareTehnicEnglezesc(mesaj: string): boolean {
    const m = mesaj.trim().toLowerCase();
    if (m.length === 0) return true;

    const areSemnRomanesc = SEMN_ROMANESC.test(m) || TIPAR_CUVINTE_ROMANESTI.test(m);
    if (areSemnRomanesc) return false;

    return FRAZE_STANDARD.includes(m)
        || TIPARE_TEHNICE.some((t) => t.test(m))
        || AMPRENTA_TEHNICA.test(m);
}

/** Codul HTTP de pe raspunsul SDK-ului (obiectul axios), cand exista. */
function statusDinRaspuns(response: unknown): number | undefined {
    const r = (response as { response?: { status?: unknown } } | null)?.response;
    const status = r?.status;
    return typeof status === 'number' ? status : undefined;
}

/** A existat un raspuns HTTP? (Distinge „backendul a raspuns" de „n-am ajuns la el".) */
function areRaspunsHttp(response: unknown): boolean {
    const r = (response as { response?: unknown } | null)?.response;
    return typeof r === 'object' && r !== null;
}

function primulTextNevid(...valori: unknown[]): string | undefined {
    for (const v of valori) {
        if (typeof v === 'string' && v.trim().length > 0) return v;
    }
    return undefined;
}

export function handleResponse<T>(response: { data?: T; error?: any }): T {
  if (response.error) {
    const error = response.error as any;

    // Erorile pe campuri, daca exista (pentru validarea de formular)
    const errors = error.errors || undefined;

    // Codul HTTP: fie din corpul erorii, fie de pe raspunsul axios
    const status = (typeof error.status === 'number' ? error.status : undefined)
      ?? statusDinRaspuns(response);

    // Mesajul brut al backendului (message > detail > title), daca e nevid
    const brut = primulTextNevid(error.message, error.detail, error.title);

    // 🔴 Nimic de la backend SI niciun raspuns HTTP ⇒ cererea n-a ajuns acolo.
    //    Inainte, cazul asta devenea `ApiValidationError('Eroare la procesarea cererii')`,
    //    iar rezervele romanesti ale apelantilor deveneau cod inaccesibil.
    if (brut === undefined && status === undefined && errors === undefined && !areRaspunsHttp(response)) {
      throw new ApiTransportError(primulTextNevid((response as { message?: unknown }).message));
    }

    // Mesaj romanesc pe ecran; cel original ramane pe obiect, pentru telemetrie
    const areMesajFolosibil = brut !== undefined && !pareTehnicEnglezesc(brut);
    const message = areMesajFolosibil ? (brut as string) : mesajGenericRomanesc(status);

    const eroare = new ApiValidationError(message, errors, status);
    // Un singur camp in plus, si numai daca serverul chiar l-a trimis ca lista de siruri.
    // Verificarea de forma nu e pedanterie: `error` e `any`, deci fara ea o valoare de alt tip
    // ar ajunge la componente ca si cum ar fi contractul.
    if (Array.isArray(error.fragmenteNerecunoscute)
        && error.fragmenteNerecunoscute.every((f: unknown) => typeof f === 'string')) {
      eroare.fragmenteNerecunoscute = error.fragmenteNerecunoscute;
    }
    if (typeof error.motiv === 'string' && error.motiv.trim().length > 0) {
      eroare.motiv = error.motiv.trim();
    }
    if (typeof error.secundePanaLaCodNou === 'number'
        && Number.isFinite(error.secundePanaLaCodNou) && error.secundePanaLaCodNou >= 0) {
      eroare.secundePanaLaCodNou = error.secundePanaLaCodNou;
    }
    if (!areMesajFolosibil && brut !== undefined) eroare.mesajOriginal = brut;
    throw eroare;
  }

  return response.data as T;
}

/**
 * Type guard pentru erorile aruncate de handleResponse.
 *
 * De ce exista: ApiValidationError era scrisa, documentata si aruncata, dar niciodata
 * folosita ca TIP. Consumatorii scriau `catch (e: any)` / `onError: (error: any)` si
 * accesau `.message` / `.errors` pe orbeste — 75 de evadari de tipuri pentru un contract
 * care era deja definit aici.
 */
export function isApiValidationError(error: unknown): error is ApiValidationError {
    return error instanceof ApiValidationError;
}

/**
 * Mesajul de afisat pentru orice eroare, cu fallback.
 *
 * Inlocuieste `getErrorMessage(error, 'fallback')` fara sa schimbe comportamentul:
 * erorile axios sunt si ele instante de Error, deci isi pastreaza mesajul.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error) return error.message || fallback;
    return fallback;
}

/**
 * Ca `getErrorMessage`, dar NU afiseaza `.message` decat daca vine de la backend
 * (`ApiValidationError`, deja in romana). Orice alta eroare (axios brut, `TypeError` de fetch)
 * poate avea `.message` in engleza si tehnic - de exemplu "Network Error" pe o cadere de retea -
 * si ajungea textual in fata utilizatorului prin `getErrorMessage`, care are incredere in
 * `.message`-ul oricarui `Error`.
 *
 * Adaugata pentru pagina publica de colaborator (planul 05, Task 14, H2): nu inlocuieste
 * `getErrorMessage`, ca sa nu schimbe comportamentul celorlalte ~75 de apeluri din aplicatie
 * care se bazeaza deja pe el.
 */
export function getSafeErrorMessage(error: unknown, fallback: string): string {
    if (isApiValidationError(error)) return error.message || fallback;
    return fallback;
}

/**
 * Erorile pe campuri, in forma ceruta de `setError` din react-hook-form.
 * Intoarce undefined pentru orice eroare care nu vine de la backend cu detalii pe campuri.
 */
export function getFieldErrors(error: unknown): Record<string, string> | undefined {
    if (isApiValidationError(error)) return error.errors;

    // Calea axios: erorile pe campuri stau in corpul raspunsului.
    const errors = axiosLike(error)?.data?.errors;
    if (typeof errors === 'object' && errors !== null) {
        return errors as Record<string, string>;
    }
    return undefined;
}

/**
 * Forma unei erori axios, cat ne intereseaza din ea.
 *
 * Nu tot codul trece prin handleResponse: cateva apeluri merg direct pe axios
 * (blob, multipart, un endpoint absent din spec), iar acolo eroarea are alta forma.
 * Citim structural, ca sa nu depindem de axios in stratul asta.
 */
function axiosLike(error: unknown): {
    status?: unknown;
    data?: { errors?: unknown; message?: unknown; detail?: unknown; title?: unknown };
} | undefined {
    if (typeof error !== 'object' || error === null) return undefined;
    const response = (error as { response?: unknown }).response;
    if (typeof response !== 'object' || response === null) return undefined;
    return response as { status?: unknown; data?: { errors?: unknown; message?: unknown; detail?: unknown; title?: unknown } };
}

/**
 * Mesajul de eroare pentru un apel axios BRUT (nu trece prin `handleResponse` — SDK-ul generat
 * nu suporta `onUploadProgress`, deci `uploadPhoto`-ul colaboratorului merge direct pe axios).
 * Acelasi lant ca `handleResponse` (message > detail > title), dar citit din corpul
 * raspunsului axios, nu din `.message`-ul lui `Error`: `error.message` pe o eroare axios e mesajul
 * TEHNIC al lui axios ("Request failed with status code 400", "Network Error"), nu mesajul
 * backendului. Fara raspuns deloc (cadere de retea), foloseste fallback-ul apelantului.
 */
export function getAxiosErrorMessage(error: unknown, fallback: string): string {
    const data = axiosLike(error)?.data;
    const message = data?.message ?? data?.detail ?? data?.title;
    return typeof message === 'string' && message.length > 0 ? message : fallback;
}

/**
 * Codul HTTP al erorii, cand se poate afla — fie de la ApiValidationError, fie de la o
 * eroare axios. Undefined pentru orice altceva (erori de retea, erori de randare,
 * valori aruncate care nu sunt Error).
 */
export function getErrorStatus(error: unknown): number | undefined {
    if (isApiValidationError(error)) return error.status;
    const status = axiosLike(error)?.status;
    return typeof status === 'number' ? status : undefined;
}
